import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Output,
  computed,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfirmationService } from '@src/app/core/services/helpers/confirmation.service';
import { SharedModule } from '@src/app/shared/shared.module';
import { OrgChartImportStateService } from '@src/app/features/dashboard/economic-groups/pages/economic-group-builder/org-chart-import-state.service';
import {
  ORG_CHART_IMPORT_STEPS,
  resolveOrgChartImportStepIndex,
} from '@src/app/features/dashboard/economic-groups/pages/economic-group-builder/org-chart-import-steps.util';

/**
 * Cartão flutuante no canto do canvas do builder com o andamento da importação de organograma (card 3578). Mostra a
 * etapa atual (`status`/`phase` do backend, atualizado pelo tempo real), vira "Pronto para revisar" com o botão
 * Revisar importação quando a proposta fica pronta e mostra o motivo quando falha. Lê e age pelo
 * `OrgChartImportStateService` provido pelo builder; só renderiza quando há importação aberta.
 */
@Component({
  selector: 'app-org-chart-import-progress-card',
  standalone: true,
  imports: [CommonModule, SharedModule],
  templateUrl: './org-chart-import-progress-card.component.html',
  styleUrls: ['./org-chart-import-progress-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrgChartImportProgressCardComponent {
  /** "Tentar de novo" depois de uma falha: o builder reabre a modal de envio. */
  @Output() retryRequested = new EventEmitter<void>();

  readonly state = inject(OrgChartImportStateService);
  private readonly confirmation = inject(ConfirmationService);
  private readonly destroyRef = inject(DestroyRef);

  readonly totalSteps = ORG_CHART_IMPORT_STEPS.length;
  readonly currentStepNumber = computed(() =>
    Math.min(resolveOrgChartImportStepIndex(this.state.current()) + 1, this.totalSteps)
  );
  /** Largura da barra: etapas concluídas mais meia etapa da que está rodando. */
  readonly progressPercent = computed(() => {
    if (this.state.isReady()) return 100;
    const index = resolveOrgChartImportStepIndex(this.state.current());
    return Math.round(((index + 0.5) / this.totalSteps) * 100);
  });
  readonly title = computed(() => {
    if (this.state.isReady()) return 'Pronto para revisar';
    if (this.state.isFailed()) return 'Não deu para ler o organograma';
    return 'Lendo o organograma';
  });
  readonly failureMessage = computed(() => {
    const item = this.state.current();
    return (
      item?.errorMessage ||
      item?.phase ||
      'A leitura parou antes de terminar. Use Tentar de novo para enviar o arquivo outra vez.'
    );
  });

  cancelImport(): void {
    this.confirmation
      .confirm(
        'Cancelar a importação?',
        'A leitura para e nada é gravado no grupo. Você pode enviar o organograma de novo depois.',
        'danger',
        'Continuar lendo',
        'Cancelar importação'
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirmed => {
        if (confirmed) this.state.discard();
      });
  }

  close(): void {
    this.state.discard();
  }

  retry(): void {
    this.retryRequested.emit();
  }

  openReview(): void {
    this.state.openReview();
  }
}
